import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import useAnalyticsWS from '../../hooks/useAnalyticsWS';
import PulseNumber from '../../components/PulseNumber';
import { BarChart3, ChevronDown, ChevronRight, RefreshCcw, Building2, Wallet, ArrowDown, ArrowUp, Gamepad2 } from 'lucide-react';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'all', label: 'All Time' },
  { id: 'custom', label: 'Custom' },
];

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtFP = (n) => `${Math.round(Number(n || 0)).toLocaleString()} FP`;

// Cross-tenant KPI + per-provider game balance view. Lives under the
// "Tenants → Analytics" nav item and powers the main-admin overview screen.
export default function TenantAnalyticsPage() {
  const [period, setPeriod] = useState('today');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // tenant_id we're expanded on
  const [providerData, setProviderData] = useState({}); // { [tenant_id]: { rows, total_load_sc, total_redeem_sc } }
  const [providerLoading, setProviderLoading] = useState({});
  const [lastTick, setLastTick] = useState(new Date());

  const qs = useMemo(() => {
    const params = new URLSearchParams({ period });
    if (period === 'custom' && since) params.set('since', new Date(since).toISOString());
    if (period === 'custom' && until) params.set('until', new Date(until).toISOString());
    return params.toString();
  }, [period, since, until]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/tenant-analytics/overview?${qs}`);
      setOverview(res.data);
      setLastTick(new Date());
    } catch (e) {
      // no-op — empty state still renders
    } finally { setLoading(false); }
  };

  const loadProviders = async (tenantId, slug) => {
    const key = tenantId || 'main';
    setProviderLoading(l => ({ ...l, [key]: true }));
    try {
      const params = new URLSearchParams({ period });
      if (tenantId) params.set('tenant_id', tenantId);
      if (period === 'custom' && since) params.set('since', new Date(since).toISOString());
      if (period === 'custom' && until) params.set('until', new Date(until).toISOString());
      const res = await api.get(`/api/admin/tenant-analytics/providers?${params.toString()}`);
      setProviderData(d => ({ ...d, [key]: res.data }));
    } catch {} finally {
      setProviderLoading(l => ({ ...l, [key]: false }));
    }
  };

  const toggleExpand = (row) => {
    const key = row.tenant_id || 'main';
    const isOpen = expanded === key;
    setExpanded(isOpen ? null : key);
    if (!isOpen && !providerData[key]) {
      loadProviders(row.tenant_id, row.tenant_slug);
    }
  };

  // Refetch when the period / custom range changes.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [qs]);

  // ── Real-time WS push for "today" (server sends snapshots every ~15s).
  //    We keep the 30s polling as a safety net below so if the socket
  //    drops we never go stale for long.
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('session_token') : null;
  const wsStatus = useAnalyticsWS({
    token: adminToken,
    enabled: period === 'today',
    onSnapshot: (snap) => {
      setOverview(snap);
      setLastTick(new Date());
    },
  });

  // Auto-refresh every 30s so KPIs feel "live" without a manual hit.
  useEffect(() => {
    const id = setInterval(() => {
      load();
      // Also refresh the currently-expanded tenant's provider panel.
      if (expanded) {
        const row = overview?.rows?.find(r => (r.tenant_id || 'main') === expanded);
        if (row) loadProviders(row.tenant_id, row.tenant_slug);
      }
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [qs, expanded]);

  const rows = overview?.rows || [];
  const grand = overview?.grand_total || { deposits_sc: 0, withdrawals_sc: 0, net_sc: 0, users: 0 };

  return (
    <div className="space-y-5" data-testid="tenant-analytics-page">
      {/* Header + period selector */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#D4AF37]" />
            Tenant Analytics
          </h1>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
            Live deposit / withdrawal KPIs & per-provider game balances · last {lastTick.toLocaleTimeString()}
            {period === 'today' && wsStatus.current === 'open' && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400"
                data-testid="analytics-live-badge"
                title="Real-time updates via WebSocket"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                LIVE
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {PERIODS.map(p => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${period === p.id ? 'bg-[#D4AF37] text-black' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
              data-testid={`period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
          <button onClick={load} className="px-2 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition" title="Refresh" data-testid="ta-refresh">
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Custom range inputs */}
      {period === 'custom' && (
        <div className="glass-card rounded-xl p-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] text-gray-500 mb-1 block">Since</label>
            <input type="datetime-local" value={since} onChange={e => setSince(e.target.value)} className="bg-[#0B0E14] border border-white/10 rounded-md px-2 py-1 text-sm" data-testid="ta-custom-since" />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 mb-1 block">Until</label>
            <input type="datetime-local" value={until} onChange={e => setUntil(e.target.value)} className="bg-[#0B0E14] border border-white/10 rounded-md px-2 py-1 text-sm" data-testid="ta-custom-until" />
          </div>
        </div>
      )}

      {/* Grand totals — Cash + FP split (Phase 3) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={Wallet} label="Users" value={(grand.users || 0).toLocaleString()} rawValue={grand.users || 0} color="#A28BFE" testid="kpi-users" />
        <KpiCard icon={ArrowDown} label="Total Deposits" value={fmtUSD(grand.deposits_usd)} rawValue={grand.deposits_usd || 0} sub={`${grand.deposits_sc ? fmtMoney(grand.deposits_sc) : ''}`} color="#22c55e" testid="kpi-deposits" />
        <KpiCard icon={ArrowUp} label="Total Withdrawals" value={fmtUSD(grand.withdrawals_usd)} rawValue={grand.withdrawals_usd || 0} sub={`${grand.withdrawals_sc ? fmtMoney(grand.withdrawals_sc) : ''}`} color="#EF4444" testid="kpi-withdrawals" />
        <KpiCard icon={BarChart3} label="Net (Cash)" value={fmtUSD((grand.deposits_usd || 0) - (grand.withdrawals_usd || 0))} rawValue={(grand.deposits_usd || 0) - (grand.withdrawals_usd || 0)} color={grand.net_sc >= 0 ? '#D4AF37' : '#EF4444'} testid="kpi-net" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kpi-fp-split">
        <KpiCard icon={Wallet} label="Cash $ Liability" value={fmtUSD(grand.cash_balance)} rawValue={grand.cash_balance || 0} color="#F5D76E" testid="kpi-cash-liability" />
        <KpiCard icon={Wallet} label="FP Outstanding" value={fmtFP(grand.fp_outstanding)} rawValue={grand.fp_outstanding || 0} sub="Live balance" color="#A5B4FC" testid="kpi-fp-outstanding" />
        <KpiCard icon={ArrowDown} label="FP Issued" value={fmtFP(grand.fp_issued)} rawValue={grand.fp_issued || 0} sub="in window" color="#8b5cf6" testid="kpi-fp-issued" />
        <KpiCard icon={ArrowUp} label="FP Converted → $" value={fmtFP(grand.fp_converted)} rawValue={grand.fp_converted || 0} sub="in window" color="#14b8a6" testid="kpi-fp-converted" />
        <KpiCard icon={Gamepad2} label="FP Spent in Games" value={fmtFP(grand.fp_spent)} rawValue={grand.fp_spent || 0} sub="in window" color="#ec4899" testid="kpi-fp-spent" />
      </div>

      {/* Per-tenant table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[#D4AF37]" />
          <h3 className="font-semibold text-sm">Per-Tenant Breakdown</h3>
          <span className="text-[10px] text-gray-500 ml-auto">{rows.length} tenants</span>
        </div>
        {loading && !overview ? (
          <div className="p-8 text-center text-gray-500 text-sm" data-testid="ta-loading">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No tenants to show.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {rows.map(row => {
              const key = row.tenant_id || 'main';
              const isOpen = expanded === key;
              const providers = providerData[key];
              const pLoading = providerLoading[key];
              return (
                <div key={key} data-testid={`tenant-row-${row.tenant_slug || 'main'}`}>
                  <button
                    onClick={() => toggleExpand(row)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition text-left"
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{row.tenant_name}</p>
                      <p className="text-[10px] text-gray-500">{row.tenant_slug} · {row.users} users</p>
                    </div>
                    <div className="hidden md:flex items-center gap-6 text-right">
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Deposits</p>
                        <p className="text-sm font-bold text-green-400"><PulseNumber value={row.deposits.usd || 0} format={fmtUSD} /></p>
                        <p className="text-[10px] text-gray-500">{row.deposits.count} reqs</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Withdrawals</p>
                        <p className="text-sm font-bold text-red-400"><PulseNumber value={row.withdrawals.usd || 0} format={fmtUSD} /></p>
                        <p className="text-[10px] text-gray-500">{row.withdrawals.count} reqs</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Cash $ Bal</p>
                        <p className="text-sm font-bold text-[#F5D76E]" data-testid={`tenant-cash-${row.tenant_slug || 'main'}`}><PulseNumber value={row.cash_balance || 0} format={fmtUSD} /></p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">FP Out.</p>
                        <p className="text-sm font-bold text-indigo-300" data-testid={`tenant-fp-${row.tenant_slug || 'main'}`}>{fmtFP(row.fp_outstanding)}</p>
                        <p className="text-[9px] text-gray-500">issued {fmtFP(row.fp_issued)}</p>
                      </div>
                    </div>
                  </button>
                  {/* Mobile-only summary row */}
                  <div className="md:hidden px-4 pb-2 grid grid-cols-4 gap-2 text-center">
                    <Mini label="Dep" value={fmtUSD(row.deposits.usd)} color="text-green-400" />
                    <Mini label="W/D" value={fmtUSD(row.withdrawals.usd)} color="text-red-400" />
                    <Mini label="Cash" value={fmtUSD(row.cash_balance)} color="text-[#F5D76E]" />
                    <Mini label="FP" value={fmtFP(row.fp_outstanding)} color="text-indigo-300" />
                  </div>
                  {isOpen && (
                    <div className="px-4 pb-4 bg-black/20">
                      <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-400">
                        <Gamepad2 className="w-3.5 h-3.5" />
                        Per-provider game balances
                      </div>
                      {pLoading ? (
                        <p className="text-[11px] text-gray-500 px-2 py-3">Loading providers…</p>
                      ) : !providers || providers.rows.length === 0 ? (
                        <p className="text-[11px] text-gray-500 px-2 py-3" data-testid={`no-providers-${row.tenant_slug || 'main'}`}>
                          No game activity for this tenant in the selected period.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs" data-testid={`provider-table-${row.tenant_slug || 'main'}`}>
                            <thead className="text-gray-500 uppercase text-[10px]">
                              <tr>
                                <th className="text-left py-1.5 pl-2">Provider</th>
                                <th className="text-right py-1.5">Load $</th>
                                <th className="text-right py-1.5">Load #</th>
                                <th className="text-right py-1.5">Redeem $</th>
                                <th className="text-right py-1.5">Redeem #</th>
                                <th className="text-right py-1.5 pr-2">Net $</th>
                              </tr>
                            </thead>
                            <tbody>
                              {providers.rows.map(p => (
                                <tr key={p.provider} className="border-t border-white/5" data-testid={`provider-row-${p.provider}`}>
                                  <td className="py-1.5 pl-2 font-semibold capitalize">{p.provider}</td>
                                  <td className="py-1.5 text-right text-purple-300 tabular-nums">{fmtMoney(p.load_sc)}</td>
                                  <td className="py-1.5 text-right text-gray-500">{p.load_count}</td>
                                  <td className="py-1.5 text-right text-green-300 tabular-nums">{fmtMoney(p.redeem_sc)}</td>
                                  <td className="py-1.5 text-right text-gray-500">{p.redeem_count}</td>
                                  <td className={`py-1.5 pr-2 text-right font-bold tabular-nums ${p.net_sc >= 0 ? 'text-[#D4AF37]' : 'text-red-400'}`}>{fmtMoney(p.net_sc)}</td>
                                </tr>
                              ))}
                              <tr className="border-t border-white/10 bg-black/30">
                                <td className="py-2 pl-2 font-bold text-gray-300">TOTAL</td>
                                <td className="py-2 text-right text-purple-300 font-bold tabular-nums">{fmtMoney(providers.total_load_sc)}</td>
                                <td />
                                <td className="py-2 text-right text-green-300 font-bold tabular-nums">{fmtMoney(providers.total_redeem_sc)}</td>
                                <td />
                                <td className={`py-2 pr-2 text-right font-bold tabular-nums ${providers.total_redeem_sc - providers.total_load_sc >= 0 ? 'text-[#D4AF37]' : 'text-red-400'}`}>{fmtMoney(providers.total_redeem_sc - providers.total_load_sc)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, rawValue, sub, color, testid }) {
  return (
    <div className="glass-card rounded-xl p-4 flex flex-col gap-1" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-400">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        {label}
      </div>
      <p className="text-xl font-bold tabular-nums" style={{ color }}>
        {rawValue !== undefined ? (
          <PulseNumber value={rawValue} format={() => value} />
        ) : (
          value
        )}
      </p>
      {sub && <p className="text-[10px] text-gray-500">{sub}</p>}
    </div>
  );
}

function Mini({ label, value, color }) {
  return (
    <div>
      <p className="text-[9px] uppercase text-gray-500">{label}</p>
      <p className={`text-[11px] font-bold tabular-nums ${color}`}>{value}</p>
    </div>
  );
}
